<script lang="ts">
export default {
  name: 'ExportDropdown',
};

export interface ExportOption {
  value: string;
  label: string;
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import DownloadButton from '@/components/v2/DownloadButton.vue';

const props = withDefaults(
  defineProps<{
    exportType: Array<string | ExportOption>;
    tooltip?: string;
    disabled?: boolean;
    /** Which edge of the button the menu lines up with; `end` makes it open toward the left. */
    menuAlign?: 'start' | 'end';
  }>(),
  { menuAlign: 'start' },
);

const emit = defineEmits<{
  (e: 'export', type: string, finishExport: () => void): void;
}>();

const menuOpen = ref<boolean>(false);
const isExporting = ref<boolean>(false);

watch(
  () => props.disabled,
  (disabled) => {
    if (disabled) menuOpen.value = false;
  },
);

const options = computed<ExportOption[]>(() =>
  props.exportType.map((type) => (typeof type === 'string' ? { value: type, label: `Export as ${type.toUpperCase()}` } : type)),
);

function handleExport(item: string) {
  isExporting.value = true;
  menuOpen.value = false;
  emit('export', item, () => (isExporting.value = false));
}

function toggleMenu() {
  menuOpen.value = !menuOpen.value;
}
</script>

<template>
  <div class="relative flex flex-col" ref="dropdownTrigger">
    <span v-if="isExporting" class="loading loading-dots loading-xs self-center"></span>
    <div v-else :class="{ 'tooltip tooltip-right': tooltip }" :data-tip="tooltip">
      <DownloadButton :disabled="disabled" @click="toggleMenu" />
    </div>

    <div
      v-if="menuOpen"
      class="menu menu-sm absolute bottom-auto top-[110%] z-90 flex w-max origin-top rounded-lg border border-solid border-gray-200 bg-base-100 shadow"
      :class="menuAlign === 'end' ? 'right-0' : 'left-0'"
    >
      <ul>
        <li v-for="option in options" :key="option.value" class="px-1 py-1" @click="handleExport(option.value)">
          <span class="text-sm">{{ option.label }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>
